import gql from 'graphql-tag';
import {
  FRAGMENT_ALERT_NOTIFICATION_SETTINGS,
  FRAGMENT_ASSET_ALERT,
  FRAGMENT_ALERT_NOTIFICATION,
  FRAGMENT_ASSET_ESSENTIAL_FIELDS,
  FRAGMENT_ASSET_FIELDS,
  FRAGMENT_ASSET_MAIN_FIELDS,
  FRAGMENT_ASSET_INFO_FIELDS,
  FRAGMENT_ASSET_SERIES_FIELDS,
  FRAGMENT_AVATAR_FIELDS,
  FRAGMENT_CHAT_MESSAGE_FIELDS,
  FRAGMENT_CHAT_MESSAGE_RESPONSE_FIELDS,
  FRAGMENT_CHATROOM_WITH_SETTINGS_FIELDS,
  FRAGMENT_KNOWLEDGE_CATEGORY_FIELDS,
  FRAGMENT_KNOWLEDGE_POST_FIELDS,
  FRAGMENT_MEDIA_FIELDS,
  FRAGMENT_MEMBERSHIP_FIELDS,
  FRAGMENT_MEMBERSHIP_PLAN_FIELDS,
  FRAGMENT_MEMBERSHIP_PLAN_WIDGET_FIELDS,
  FRAGMENT_PROFILE_FIELDS,
  FRAGMENT_QR_AUTH_FIELDS,
  FRAGMENT_STRATEGY_ASSET_CATEGORY_FIELDS,
  FRAGMENT_STRATEGY_ASSET_FIELDS,
  FRAGMENT_STRATEGY_ASSET_TARGET_FIELDS,
  FRAGMENT_STRATEGY_FIELDS,
  FRAGMENT_STRATEGY_RISK_CONCENTRATION_WEIGHTS,
  FRAGMENT_STRATEGY_RISK_FIELDS,
  FRAGMENT_USER_FIELDS,
  FRAGMENT_WIDGET_FIELDS,
  FRAGMENT_WORKSPACE_FIELDS,
  FRAGMENT_WORKSPACE_WIDGET_FIELDS,
  FRAGMENT_VARIATIONS,
} from './apollo-fragments';

/* USER */

export const CREATE_USER = gql`
  mutation createUser($language: Language!, $referralCode: String) {
    createUser(language: $language, referralCode: $referralCode) {
      id
    }
  }
`;

export const CHECK_USER_EXISTS = gql`
  query checkUserExist($email: AWSEmail!) {
    checkUserExist(email: $email) {
      status
    }
  }
`;

export const COMPLETE_TOTP_BYPASS = gql`
  mutation completeTOTPBypass($code: String!, $bypassToken: String!) {
    completeTOTPBypass(code: $code, bypassToken: $bypassToken) {
      status
    }
  }
`;

export const DISABLE_MASTER_KEY = gql`
  mutation disableMasterKey {
    disableMasterKey {
      status
    }
  }
`;

export const ENABLE_MASTER_KEY = gql`
  mutation enableMasterKey {
    enableMasterKey {
      key
    }
  }
`;

export const ME = gql`
  query me {
    me {
      ...UserFields
      profiles {
        items {
          ...ProfileFields
          avatar {
            ...AvatarFields
          }
        }
      }
      membership {
        ...MembershipFields
        membershipPlan {
          ...MembershipPlanFields
        }
      }
    }
  }
  ${FRAGMENT_MEMBERSHIP_PLAN_FIELDS}
  ${FRAGMENT_USER_FIELDS}
  ${FRAGMENT_PROFILE_FIELDS}
  ${FRAGMENT_MEMBERSHIP_FIELDS}
  ${FRAGMENT_AVATAR_FIELDS}
`;

export const LOGIN_WITH_GOOGLE = gql`
  mutation loginWithGoogle(
    $email: AWSEmail!
    $googleId: String!
    $token: String!
  ) {
    loginWithGoogle(email: $email, googleId: $googleId, token: $token) {
      id
      otpPassword
    }
  }
`;

export const LOGIN_WITH_TWITTER = gql`
  mutation loginWithTwitter(
    $email: AWSEmail!
    $twitterId: String!
    $token: String!
  ) {
    loginWithTwitter(email: $email, twitterId: $twitterId, token: $token) {
      id
      otpPassword
    }
  }
`;

export const REQUEST_TOTP_BYPASS = gql`
  mutation requestTOTPBypass($email: AWSEmail!, $masterKey: String!) {
    requestTOTPBypass(email: $email, masterKey: $masterKey) {
      bypassToken
      expireAt
    }
  }
`;

export const SET_SIGNUP_SLIDE_AS_SEEN = gql`
  mutation setSignupSlideAsSeen {
    setSignupSlideAsSeen {
      status
    }
  }
`;

export const SET_SUBSCRIPTION_CHOSEN = gql`
  mutation setSubscriptionChosen {
    setSubscriptionChosen {
      status
    }
  }
`;

export const SIGNUP_WITH_TWITTER = gql`
  mutation signupWithTwitter(
    $email: AWSEmail!
    $twitterId: String!
    $token: String!
  ) {
    signupWithTwitter(email: $email, twitterId: $twitterId, token: $token) {
      id
      otpPassword
    }
  }
`;

export const SIGNUP_WITH_GOOGLE = gql`
  mutation signupWithGoogle(
    $email: AWSEmail!
    $googleId: String!
    $token: String!
  ) {
    signupWithGoogle(email: $email, googleId: $googleId, token: $token) {
      id
      otpPassword
    }
  }
`;

/* PROFILE */

export const CHANGE_DEFAULT_PROFILE_USERNAME = gql`
  mutation changeDefaultProfileUsername($username: String!) {
    changeDefaultProfileUsername(username: $username) {
      id
    }
  }
`;

export const CREATE_DEFAULT_PROFILE = gql`
  mutation createDefaultProfile($username: String!) {
    createDefaultProfile(username: $username) {
      id
    }
  }
`;

export const CREATE_ADDITIONAL_PROFILE = gql`
  mutation createAdditionalProfile($username: String!) {
    createAdditionalProfile(username: $username) {
      id
    }
  }
`;

export const DELETE_ADDITIONAL_PROFILE = gql`
  mutation deleteAdditionalProfile($profileId: ID!) {
    deleteAdditionalProfile(profileId: $profileId) {
      id
    }
  }
`;

export const SET_CURRENT_PROFILE = gql`
  mutation setCurrentProfile($profileId: ID!) {
    setCurrentProfile(profileId: $profileId) {
      id
    }
  }
`;

export const SET_DEFAULT_PROFILE_AVATAR = gql`
  mutation setDefaultProfileAvatar($profileId: ID!, $mediaId: ID!) {
    setDefaultProfileAvatar(profileId: $profileId, mediaId: $mediaId) {
      id
    }
  }
`;

export const SET_PROFILE_AVATAR = gql`
  mutation setProfileAvatar($profileId: ID!, $mediaId: ID!) {
    setProfileAvatar(profileId: $profileId, mediaId: $mediaId) {
      id
    }
  }
`;

export const SET_PROFILE_COMPLETED = gql`
  mutation setProfileCompleted($profileId: ID!) {
    setProfileCompleted(profileId: $profileId) {
      id
    }
  }
`;

export const SET_TUTORIAL_SEEN = gql`
  mutation setTutorialSeen($profileId: ID!, $tutorial: Tutorial!) {
    setTutorialSeen(profileId: $profileId, tutorial: $tutorial) {
      id
    }
  }
`;

export const SET_UI_MODE = gql`
  mutation setUIMode($mode: UIMode!) {
    setUIMode(mode: $mode) {
      status
    }
  }
`;

export const UPDATE_ADDITIONAL_PROFILE = gql`
  mutation updateAdditionalProfile($profileId: ID!, $username: String!) {
    updateAdditionalProfile(profileId: $profileId, username: $username) {
      id
    }
  }
`;

/* WORKSPACE */

export const ADD_WIDGET_TO_WORKSPACE = gql`
  mutation addWidgetToWorkspace(
    $size: WidgetSize!
    $widgetId: ID!
    $workspaceId: ID!
  ) {
    addWidgetToWorkspace(
      size: $size
      widgetId: $widgetId
      workspaceId: $workspaceId
    ) {
      ...WorkspaceWidgetFields
      widget {
        ...WidgetFields
      }
    }
  }
  ${FRAGMENT_WORKSPACE_WIDGET_FIELDS}
  ${FRAGMENT_WIDGET_FIELDS}
`;

export const GET_WORKSPACE = gql`
  query getWorkspace($workspaceId: ID!) {
    getWorkspace(id: $workspaceId) {
      ...WorkspaceFields
      workspaceWidgets {
        items {
          ...WorkspaceWidgetFields
          widget {
            ...WidgetFields
            icon {
              ...MediaFields
            }
            images {
              items {
                ...MediaFields
              }
            }
            membershipPlans {
              items {
                position
                membershipPlan {
                  ...MembershipPlanFields
                }
              }
            }
          }
        }
      }
    }
  }
  ${FRAGMENT_WORKSPACE_FIELDS}
  ${FRAGMENT_WORKSPACE_WIDGET_FIELDS}
  ${FRAGMENT_WIDGET_FIELDS}
  ${FRAGMENT_MEDIA_FIELDS}
  ${FRAGMENT_MEMBERSHIP_PLAN_FIELDS}
`;

export const DELETE_WORKSPACE = gql`
  mutation deleteWorkspace($workspaceId: ID!, $profileId: String!) {
    deleteWorkspace(workspaceId: $workspaceId, profileId: $profileId) {
      id
    }
  }
`;

export const LIST_DEFAULT_WORKSPACES = gql`
  query listWorkspaces {
    listWorkspaces {
      items {
        ...WorkspaceFields
        cover {
          ...MediaFields
        }
      }
    }
  }
  ${FRAGMENT_MEDIA_FIELDS}
  ${FRAGMENT_WORKSPACE_FIELDS}
`;

export const LIST_USER_WORKSPACES = gql`
  query listWorkspaces($profileId: ID) {
    listWorkspaces(profileId: $profileId) {
      items {
        ...WorkspaceFields
      }
    }
  }
  ${FRAGMENT_WORKSPACE_FIELDS}
`;

export const LIST_WIDGETS = gql`
  query listWidgets {
    listWidgets {
      items {
        ...WidgetFields
        membershipPlans {
          items {
            ...MembershipPlanWidgetFields
            membershipPlan {
              ...MembershipPlanFields
            }
          }
        }
        icon {
          ...MediaFields
        }
        images {
          items {
            ...MediaFields
          }
        }
      }
    }
  }
  ${FRAGMENT_WIDGET_FIELDS}
  ${FRAGMENT_MEDIA_FIELDS}
  ${FRAGMENT_MEMBERSHIP_PLAN_FIELDS}
  ${FRAGMENT_MEMBERSHIP_PLAN_WIDGET_FIELDS}
`;

export const REMOVE_WIDGET_FROM_WORKSPACE = gql`
  mutation removeWidgetFromWorkspace(
    $workspaceId: ID!
    $workspaceWidgetId: ID!
  ) {
    removeWidgetFromWorkspace(
      workspaceId: $workspaceId
      workspaceWidgetId: $workspaceWidgetId
    ) {
      status
    }
  }
`;

export const SAVE_WORKSPACE = gql`
  mutation saveWorkspace(
    $workspaceId: ID!
    $profileId: String!
    $name: String!
  ) {
    saveWorkspace(
      workspaceId: $workspaceId
      profileId: $profileId
      name: $name
    ) {
      id
    }
  }
`;

export const SEARCH_WIDGETS = gql`
  query searchWidgets($term: String!) {
    searchWidgets(term: $term) {
      items {
        id
      }
    }
  }
`;

export const SET_WORKSPACE_WIDGET_POSITION = gql`
  mutation setWorkspaceWidgetPosition(
    $workspaceWidgetId: ID!
    $workspaceId: ID!
    $position: Int!
  ) {
    setWorkspaceWidgetPosition(
      workspaceWidgetId: $workspaceWidgetId
      workspaceId: $workspaceId
      position: $position
    ) {
      id
      position
    }
  }
`;

export const SET_WORKSPACE_WIDGETS_POSITIONS = gql`
  mutation setWorkspaceWidgetsPositions(
    $workspaceWidgetIds: [ID]!
    $workspaceId: ID!
  ) {
    setWorkspaceWidgetsPositions(
      workspaceWidgetIds: $workspaceWidgetIds
      workspaceId: $workspaceId
    ) {
      status
    }
  }
`;

export const SET_DEFAULT_WORKSPACE = gql`
  mutation setDefaultWorkspace($profileId: ID!, $workspaceId: ID!) {
    setDefaultWorkspace(profileId: $profileId, workspaceId: $workspaceId) {
      id
    }
  }
`;

export const SET_WORKSPACE_WIDGET_SIZE = gql`
  mutation setWorkspaceWidgetSize($workspaceWidgetId: ID!, $size: WidgetSize!) {
    setWorkspaceWidgetSize(workspaceWidgetId: $workspaceWidgetId, size: $size) {
      id
      size
    }
  }
`;

/* MEMBERSHIP */

export const CHECKOUT_START = gql`
  mutation checkoutStart($input: CheckoutStartInput!) {
    checkoutStart(input: $input) {
      id
      type
      url
      state
      embed
      createdAt
      expiresAt
      object
      updatedAt
      resourceVersion
      token
    }
  }
`;

export const CHECKOUT_COMPLETE = gql`
  mutation checkoutComplete($input: CheckoutCompleteInput!) {
    checkoutComplete(input: $input) {
      membership {
        id
        cbId
        membershipPlanId
      }
      action
    }
  }
`;

export const GENERATE_PORTAL_SESSION = gql`
  mutation generatePortalSession {
    generatePortalSession {
      id
      token
      accessUrl
      status
      createdAt
      expiresAt
      object
      customerId
      linkedCustomers {
        object
        customerId
        email
        hasBillingAddress
        hasPaymentMethod
        hasActiveSubscription
      }
    }
  }
`;

export const GET_MEMBERSHIP_BILLING_ADDRESS = gql`
  query getMembershipBillingAddress {
    getMembershipBillingAddress {
      firstName
      lastName
      line1
      line2
      city
      state
      country
      zip
    }
  }
`;

export const GET_MEMBERSHIP_INVOICE_URL = gql`
  query getMembershipInvoiceUrl($invoiceId: ID!) {
    getMembershipInvoiceUrl(invoiceId: $invoiceId) {
      downloadUrl
    }
  }
`;

export const GET_MEMBERSHIP_PLAN = gql`
  query getMembershipPlan($id: ID!) {
    getMembershipPlan(id: $id) {
      ...MembershipPlanFields
    }
  }
  ${FRAGMENT_MEMBERSHIP_PLAN_FIELDS}
`;

export const LIST_MEMBERSHIP_INVOICES = gql`
  query listMembershipInvoices {
    listMembershipInvoices {
      items {
        id
        date
        object
        sku
        status
        total
        membershipPlan {
          ...MembershipPlanFields
        }
      }
    }
  }
  ${FRAGMENT_MEMBERSHIP_PLAN_FIELDS}
`;

export const LIST_MEMBERSHIP_PLANS = gql`
  query listMembershipPlans {
    listMembershipPlans {
      items {
        ...MembershipPlanFields
        membershipPlanWidgets {
          items {
            ...MembershipPlanWidgetFields
            widget {
              ...WidgetFields
            }
          }
        }
      }
    }
  }
  ${FRAGMENT_MEMBERSHIP_PLAN_WIDGET_FIELDS}
  ${FRAGMENT_MEMBERSHIP_PLAN_FIELDS}
  ${FRAGMENT_WIDGET_FIELDS}
`;

export const LIST_MEMBERSHIP_PAYMENT_SOURCES = gql`
  query listMembershipPaymentSources {
    listMembershipPaymentSources {
      items {
        maskedNumber
        brand
        expiryYear
        expiryMonth
        object
      }
    }
  }
`;

/* STRATEGY */

export const ARCHIVE_STRATEGY = gql`
  mutation disableStrategy($strategyId: ID!) {
    disableStrategy(strategyId: $strategyId) {
      id
    }
  }
`;

export const CLONE_STRATEGY = gql`
  mutation cloneStrategy($strategyId: ID!, $name: String!) {
    cloneStrategy(strategyId: $strategyId, name: $name) {
      id
    }
  }
`;

export const CLONE_STRATEGY_ASSET = gql`
  mutation cloneStrategyAsset($strategyAssetId: ID!) {
    cloneStrategyAsset(strategyAssetId: $strategyAssetId) {
      id
    }
  }
`;

export const COPY_STRATEGY_ASSETS = gql`
  mutation copyStrategyAssets(
    $strategyAssetIds: [ID]!
    $targetStrategyId: ID!
  ) {
    copyStrategyAssets(
      strategyAssetIds: $strategyAssetIds
      targetStrategyId: $targetStrategyId
    ) {
      status
    }
  }
`;

export const CREATE_STRATEGY = gql`
  mutation createStrategy($input: CreateStrategyInput!) {
    createStrategy(input: $input) {
      ...StrategyFields
    }
  }
  ${FRAGMENT_STRATEGY_FIELDS}
`;

export const CREATE_STRATEGY_ASSET = gql`
  mutation createStrategyAsset($input: [CreateStrategyAssetInput]!) {
    createStrategyAsset(input: $input) {
      id
    }
  }
`;

export const CREATE_STRATEGY_ASSETS = gql`
  mutation createStrategyAssets($input: [CreateStrategyAssetInput]!) {
    createStrategyAsset(input: $input) {
      id
    }
  }
`;

export const CREATE_STRATEGY_ASSET_CATEGORY = gql`
  mutation createStrategyAssetCategory(
    $input: CreateStrategyAssetCategoryInput!
  ) {
    createStrategyAssetCategory(input: $input) {
      id
    }
  }
`;

export const DELETE_STRATEGY = gql`
  mutation deleteStrategy($strategyId: ID!) {
    deleteStrategy(strategyId: $strategyId) {
      id
    }
  }
`;

export const DELETE_STRATEGY_ASSET = gql`
  mutation deleteStrategyAsset($strategyId: ID!, $strategyAssetId: ID!) {
    deleteStrategyAsset(
      strategyId: $strategyId
      strategyAssetId: $strategyAssetId
    ) {
      id
    }
  }
`;

export const DELETE_STRATEGY_ASSETS = gql`
  mutation deleteStrategyAssets($strategyId: ID!, $strategyAssetIds: [ID]!) {
    deleteStrategyAssets(
      strategyId: $strategyId
      strategyAssetIds: $strategyAssetIds
    ) {
      status
    }
  }
`;

export const DELETE_STRATEGY_ASSET_TARGET = gql`
  mutation deleteStrategyAssetTarget($strategyAssetTargetId: ID!) {
    deleteStrategyAssetTarget(strategyAssetTargetId: $strategyAssetTargetId) {
      id
    }
  }
`;

export const LIST_STRATEGIES_BY_PROFILE = gql`
  query listStrategiesByProfile($profileId: ID!) {
    listStrategiesByProfile(profileId: $profileId) {
      items {
        ...StrategyFields
        strategyAssets {
          items {
            ...StrategyAssetFields
            strategyAssetTargets {
              items {
                ...StrategyAssetTargetFields
              }
            }
            strategyAssetCategory {
              ...StrategyAssetCategoryFields
            }
            asset {
              ...AssetFields
              history {
                ...AssetSeriesFields
              }
              info {
                ...AssetInfoFields
              }
            }
          }
        }
        workspace {
          ...WorkspaceFields
          workspaceWidgets {
            items {
              ...WorkspaceWidgetFields
              widget {
                ...WidgetFields
              }
            }
          }
        }
      }
    }
  }
  ${FRAGMENT_STRATEGY_FIELDS}
  ${FRAGMENT_STRATEGY_ASSET_FIELDS}
  ${FRAGMENT_STRATEGY_ASSET_TARGET_FIELDS}
  ${FRAGMENT_STRATEGY_ASSET_CATEGORY_FIELDS}
  ${FRAGMENT_ASSET_FIELDS}
  ${FRAGMENT_ASSET_SERIES_FIELDS}
  ${FRAGMENT_ASSET_INFO_FIELDS}
  ${FRAGMENT_WORKSPACE_FIELDS}
  ${FRAGMENT_WORKSPACE_WIDGET_FIELDS}
  ${FRAGMENT_WIDGET_FIELDS}
`;

export const LIST_STRATEGY_ASSET_CATEGORY_ICONS = gql`
  query listStrategyAssetCategoryIcons {
    listStrategyAssetCategoryIcons {
      items {
        id
        key
        size {
          original
        }
      }
    }
  }
`;

export const LIST_STRATEGY_ASSET_CATEGORIES_BY_PROFILE = gql`
  query listStrategyAssetCategories($profileId: ID!) {
    listStrategyAssetCategories(profileId: $profileId) {
      items {
        ...StrategyAssetCategoryFields
      }
    }
  }
  ${FRAGMENT_STRATEGY_ASSET_CATEGORY_FIELDS}
`;

export const RESUME_STRATEGY = gql`
  mutation enableStrategy($strategyId: ID!) {
    enableStrategy(strategyId: $strategyId) {
      id
    }
  }
`;

export const SEARCH_KNOWLEDGE_POSTS = gql`
  query searchKnowledgePosts($term: String!, $language: Language!) {
    searchKnowledgePosts(term: $term, language: $language) {
      items {
        ...KnowledgePostFields
      }
    }
  }
  ${FRAGMENT_KNOWLEDGE_POST_FIELDS}
`;

export const SET_STRATEGY_ACTIVE = gql`
  mutation setStrategyActive($strategyId: ID!, $active: Boolean!) {
    setStrategyActive(strategyId: $strategyId, active: $active) {
      id
    }
  }
`;

export const SET_STRATEGY_ASSET_ENTRY_PRICE = gql`
  mutation setStrategyAssetEntryPrice(
    $strategyAssetId: ID!
    $entryPrice: Float!
  ) {
    setStrategyAssetEntryPrice(
      strategyAssetId: $strategyAssetId
      entryPrice: $entryPrice
    ) {
      id
    }
  }
`;

export const SET_STRATEGY_ASSET_LAST_PRICE = gql`
  mutation setStrategyAssetLastPrice(
    $strategyAssetId: ID!
    $lastPrice: Float!
  ) {
    setStrategyAssetLastPrice(
      strategyAssetId: $strategyAssetId
      lastPrice: $lastPrice
    ) {
      id
    }
  }
`;

export const SET_STRATEGY_ASSET_UNIT = gql`
  mutation setStrategyAssetUnit($strategyAssetId: ID!, $unit: Float!) {
    setStrategyAssetUnit(strategyAssetId: $strategyAssetId, unit: $unit) {
      id
    }
  }
`;

export const SET_STRATEGY_NAME = gql`
  mutation setStrategyName($strategyId: ID!, $name: String!) {
    setStrategyName(strategyId: $strategyId, name: $name) {
      id
    }
  }
`;

export const SET_STRATEGY_RISK_ASSET_CONCENTRATION = gql`
  mutation setStrategyRiskAssetConcentration($strategyId: ID!, $value: Int!) {
    setStrategyRiskAssetConcentration(strategyId: $strategyId, value: $value) {
      ...StrategyRiskFields
    }
  }
  ${FRAGMENT_STRATEGY_RISK_FIELDS}
`;

export const SET_STRATEGY_RISK_GEOGRAPHICAL_CONCENTRATION = gql`
  mutation setStrategyRiskGeographicalConcentration(
    $strategyId: ID!
    $value: Int!
  ) {
    setStrategyRiskGeographicalConcentration(
      strategyId: $strategyId
      value: $value
    ) {
      ...StrategyRiskFields
    }
  }
  ${FRAGMENT_STRATEGY_RISK_FIELDS}
`;

export const SET_STRATEGY_RISK_CURRENCY_CONCENTRATION = gql`
  mutation setStrategyRiskCurrencyConcentration(
    $strategyId: ID!
    $value: Int!
  ) {
    setStrategyRiskCurrencyConcentration(
      strategyId: $strategyId
      value: $value
    ) {
      ...StrategyRiskFields
    }
  }
  ${FRAGMENT_STRATEGY_RISK_FIELDS}
`;

export const SET_STRATEGY_RISK_INDUSTRY_CONCENTRATION = gql`
  mutation setStrategyRiskIndustryConcentration(
    $strategyId: ID!
    $value: Int!
  ) {
    setStrategyRiskIndustryConcentration(
      strategyId: $strategyId
      value: $value
    ) {
      ...StrategyRiskFields
    }
  }
  ${FRAGMENT_STRATEGY_RISK_FIELDS}
`;

export const SET_STRATEGY_RISK_WEIGHTS = gql`
  mutation setStrategyRiskWeights(
    $strategyId: ID!
    $riskIndustryConcentrationWeight: Int!
    $riskCurrencyConcentrationWeight: Int!
    $riskAssetConcentrationWeight: Int!
    $riskGeographicalConcentrationWeight: Int!
  ) {
    setStrategyRiskWeights(
      strategyId: $strategyId
      riskIndustryConcentrationWeight: $riskIndustryConcentrationWeight
      riskCurrencyConcentrationWeight: $riskCurrencyConcentrationWeight
      riskAssetConcentrationWeight: $riskAssetConcentrationWeight
      riskGeographicalConcentrationWeight: $riskGeographicalConcentrationWeight
    ) {
      ...StrategyRiskWeights
    }
  }
  ${FRAGMENT_STRATEGY_RISK_CONCENTRATION_WEIGHTS}
`;

export const SET_STRATEGY_STATUS = gql`
  mutation setStrategyStatus($strategyId: ID!, $status: StrategyStatus!) {
    setStrategyStatus(strategyId: $strategyId, status: $status) {
      id
    }
  }
`;

export const SET_STRATEGY_TAGS = gql`
  mutation setStrategyTags($strategyId: ID!, $tags: [String]!) {
    setStrategyTags(strategyId: $strategyId, tags: $tags) {
      id
    }
  }
`;

export const SET_STRATEGY_WORKSPACE = gql`
  mutation setStrategyWorkspace(
    $strategyId: ID!
    $profileId: ID!
    $currentWorkspaceId: ID!
    $targetWorkspaceId: ID!
  ) {
    setStrategyWorkspace(
      strategyId: $strategyId
      profileId: $profileId
      currentWorkspaceId: $currentWorkspaceId
      targetWorkspaceId: $targetWorkspaceId
    ) {
      id
    }
  }
`;

export const UPDATE_STRATEGY = gql`
  mutation updateStrategy($input: UpdateStrategyInput!) {
    updateStrategy(input: $input) {
      ...StrategyFields
    }
  }
  ${FRAGMENT_STRATEGY_FIELDS}
`;

export const UPDATE_STRATEGY_ASSETS = gql`
  mutation updateStrategyAssets($input: [UpdateStrategyAssetInput]!) {
    updateStrategyAssets(input: $input) {
      id
    }
  }
`;

export const UPDATE_ASSET_ADV_SETT = gql`
  mutation updateStrategyAssetAdvancedSettings(
    $input: UpdateStrategyAssetAdvancedSettingsInput!
  ) {
    updateStrategyAssetAdvancedSettings(input: $input) {
      id
    }
  }
`;

/* KNOWLEDGE */

export const LIST_KNOWLEDGE_CATEGORIES = gql`
  query listKnowledgeCategories {
    listKnowledgeCategories {
      items {
        ...KnowledgeCategoryFields
        knowledgePosts {
          items {
            ...KnowledgePostFields
          }
        }
      }
    }
  }
  ${FRAGMENT_KNOWLEDGE_POST_FIELDS}
  ${FRAGMENT_KNOWLEDGE_CATEGORY_FIELDS}
`;

/* CHAT */

export const CLEAR_CHATROOM_NOTIFICATION = gql`
  mutation clearNotification($notificationId: ID!) {
    clearNotification(notificationId: $notificationId) {
      status
    }
  }
`;

export const CREATE_CHAT_MESSAGE = gql`
  mutation createChatMessage(
    $content: String!
    $chatRoomId: ID!
    $type: MessageType!
    $resource: String
  ) {
    createChatMessage(
      input: {
        content: $content
        chatRoomId: $chatRoomId
        type: $type
        resource: $resource
      }
    ) {
      ...ChatMessageResponseFields
      action
    }
  }
  ${FRAGMENT_CHAT_MESSAGE_RESPONSE_FIELDS}
`;

export const CREATE_MENTION = gql`
  mutation createChatMessage(
    $chatRoomId: ID!
    $content: String!
    $targetUserIds: [ID]!
    $targetUserProfiles: [TargetUserProfile]!
  ) {
    createChatMessage(
      input: {
        chatRoomId: $chatRoomId
        content: $content
        targetUserIds: $targetUserIds
        targetUserProfiles: $targetUserProfiles
        type: MENTION
      }
    ) {
      id
      action
      chatRoomId
      content
      ownerId
      sentAt
      ownerProfile {
        id
        name
      }
      type
      targetUserIds
      targetUserProfiles {
        id
        name
        ownerId
      }
    }
  }
`;

export const DELETE_CHAT_MESSAGE = gql`
  mutation deleteChatMessage($chatRoomId: ID!, $messageId: ID!) {
    deleteChatMessage(
      input: { chatRoomId: $chatRoomId }
      messageId: $messageId
    ) {
      ...ChatMessageResponseFields
    }
  }
  ${FRAGMENT_CHAT_MESSAGE_RESPONSE_FIELDS}
`;

export const LIST_CHATROOMS = gql`
  query listChatRooms($limit: Int!, $nextToken: String) {
    listChatRooms(limit: $limit, nextToken: $nextToken) {
      items {
        ...ChatRoomWithSettingsFields
        lastMessage {
          id
          content
          ownerProfile {
            id
            name
          }
          type
        }
      }
    }
  }
  ${FRAGMENT_CHATROOM_WITH_SETTINGS_FIELDS}
`;

export const LIST_CHATROOM_MESSAGES = gql`
  query listChatRoomMessages($chatRoomId: ID!, $limit: Int!) {
    listChatRoomMessages(chatRoomId: $chatRoomId, limit: $limit) {
      nextToken
      items {
        ...ChatMessageFields
      }
    }
  }
  ${FRAGMENT_CHAT_MESSAGE_FIELDS}
`;

export const LIST_CHATROOM_MESSAGES_BY_DATE = gql`
  query listChatMessagesByDate(
    $chatRoomId: ID!
    $date: AWSDateTime!
    $dir: ListDirection!
    $limit: Int!
    $sort: ListSort!
  ) {
    listMessagesByDate(
      chatRoomId: $chatRoomId
      date: $date
      dir: $dir
      limit: $limit
      sort: $sort
    ) {
      nextToken
      items {
        ...ChatMessageFields
      }
    }
  }
  ${FRAGMENT_CHAT_MESSAGE_FIELDS}
`;

export const SEARCH_CHATROOM_PROFILES = gql`
  query searchProfiles($term: String!) {
    searchProfiles(term: $term, limit: 100) {
      items {
        id
        name
        owner
      }
    }
  }
`;

export const SEARCH_CHATROOM_MESSAGES = gql`
  query searchMessages($term: String!, $chatRoomId: ID!) {
    searchChatMessages(term: $term, chatRoomId: $chatRoomId, limit: 10) {
      nextToken
      items {
        ...ChatMessageFields
      }
    }
  }
  ${FRAGMENT_CHAT_MESSAGE_FIELDS}
`;

export const LIST_CHAT_NOTIFICATIONS = gql`
  query listChatNotifications {
    listChatNotifications {
      nextToken
      items {
        ownerId
        fromUserId
        fromUserProfile {
          id
          name
        }
        seen
        chatMessage {
          chatRoomId
          id
          content
          sentAt
        }
        id
      }
    }
  }
`;

export const LIST_MUTED_USERS = gql`
  query listMutedUsers {
    listMutedUsers {
      name
      owner
    }
  }
`;

export const MARK_CHATROOM_NOTIFICATION_SEEN = gql`
  mutation markNotificationSeen($notificationId: ID!) {
    markNotificationSeen(notificationId: $notificationId) {
      status
    }
  }
`;

export const MUTE_USER = gql`
  mutation muteChatUser($profileId: ID!, $userId: ID!) {
    muteChatUser(profileId: $profileId, userId: $userId) {
      status
    }
  }
`;

export const ON_CHAT_MESSAGE = gql`
  subscription onChatMessage {
    onChatMessage {
      action
      chatRoomId
      content
      id
      ownerId
      ownerProfile {
        id
        name
      }
      targetUserIds
      sentAt
      resource
      type
      targetUserProfiles {
        id
        name
        ownerId
      }
    }
  }
`;

export const REPORT_USER = gql`
  mutation reportUser($targetUserId: ID!, $text: String!) {
    reportContactRequest(targetUserId: $targetUserId, text: $text) {
      id
      owner
      text
    }
  }
`;

export const UNMUTE_USER = gql`
  mutation unmuteUser($userId: ID!) {
    unmuteChatUser(userId: $userId) {
      status
    }
  }
`;

export const UPDATE_CHATROOM_SETTINGS = gql`
  mutation updateChatSettings(
    $chatRoomId: ID!
    $notificationsEnabled: Boolean!
    $soundEnabled: Boolean!
  ) {
    updateChatSettings(
      input: {
        chatRoomId: $chatRoomId
        notificationsEnabled: $notificationsEnabled
        soundEnabled: $soundEnabled
      }
    ) {
      chatRoomId
      notificationsEnabled
      soundEnabled
    }
  }
`;

export const UPDATE_CHAT_MESSAGE = gql`
  mutation updateChatMessage(
    $chatRoomId: ID!
    $content: String!
    $messageId: ID!
  ) {
    updateChatMessage(
      input: { chatRoomId: $chatRoomId, content: $content, type: MESSAGE }
      messageId: $messageId
    ) {
      ...ChatMessageFields
    }
  }
  ${FRAGMENT_CHAT_MESSAGE_FIELDS}
`;

export const UPDATE_LAST_MESSAGE_SEEN = gql`
  mutation updateLastMessageSeen(
    $chatRoomId: ID!
    $id: ID!
    $sentAt: AWSDateTime!
  ) {
    updateChatSettings(
      input: {
        chatRoomId: $chatRoomId
        lastMessageSeen: { id: $id, sentAt: $sentAt }
      }
    ) {
      chatRoomId
    }
  }
`;

/* NOTIFICATIONS */

export const CLEAR_ASSET_ALERTS = gql`
  mutation ClearAssetAlerts {
    clearAssetAlerts {
      status
    }
  }
`;

export const CLEAR_ALERT_NOTIFICATIONS = gql`
  mutation clearAlertNotifications($amount: Int) {
    clearAlertNotifications(amount: $amount) {
      status
    }
  }
`;

export const MARK_ALERT_NOTIFICATIONS_AS_READ = gql`
  mutation markAlertNotificationsAsRead {
    markAlertNotificationsAsRead {
      status
    }
  }
`;

export const CREATE_ASSET_ALERT = gql`
  mutation createAssetAlert(
    $assetId: ID!
    $expireAt: AWSDateTime!
    $name: String
    $value: Float!
  ) {
    createAssetAlert(
      input: {
        assetId: $assetId
        expireAt: $expireAt
        name: $name
        value: $value
      }
    ) {
      id
    }
  }
`;

export const DELETE_ASSET_ALERT = gql`
  mutation deleteAssetAlert($alertId: ID!) {
    deleteAssetAlert(alertId: $alertId) {
      status
    }
  }
`;

export const GET_ALERT_NOTIFICATION_SETTINGS = gql`
  query getAlertNotificationSettings {
    getAlertNotificationSettings {
      ...AlertNotificationSettingsField
    }
  }
  ${FRAGMENT_ALERT_NOTIFICATION_SETTINGS}
`;

export const LIST_ALERT_NOTIFICATIONS = gql`
  query listAlertNotifications($nextToken: String, $limit: Int) {
    listAlertNotifications(nextToken: $nextToken, limit: $limit) {
      nextToken
      items {
        ...AssetAlertNotificationFields
      }
    }
  }
  ${FRAGMENT_ALERT_NOTIFICATION}
`;

export const GET_UNREAD_NOTIFICATIONS = gql`
  query getUnreadAlertNotificationsCount {
    getUnreadAlertNotificationsCount
  }
`;

export const LIST_ASSET_ALERTS = gql`
  query listAssetAlerts {
    listAssetAlerts {
      items {
        ...AssetAlert
      }
    }
  }
  ${FRAGMENT_ASSET_ALERT}
`;

export const ON_ASSET_ALERT = gql`
  subscription onAlertNotification($owner: ID!) {
    onAlertNotification(owner: $owner) {
      ...AssetAlertNotificationFields
    }
  }
  ${FRAGMENT_ALERT_NOTIFICATION}
`;

export const ON_CLAIM_QR = gql`
  subscription onClaimQR($id: ID!) {
    onClaimQR(id: $id) {
      ...QRAuthFields
    }
  }
  ${FRAGMENT_QR_AUTH_FIELDS}
`;

export const SEARCH_ASSET_ALERTS = gql`
  query searchAssetAlerts($term: String!) {
    searchAssetAlerts(term: $term) {
      items {
        ...AssetAlert
      }
    }
  }
  ${FRAGMENT_ASSET_ALERT}
`;

export const SEARCH_ALERT_NOTIFICATIONS = gql`
  query searchAlertNotifications($term: String!, $limit: Int = 10) {
    searchAlertNotifications(term: $term, limit: $limit) {
      items {
        ...AssetAlertNotificationFields
      }
    }
  }
  ${FRAGMENT_ALERT_NOTIFICATION}
`;

export const UPDATE_ASSET_ALERT = gql`
  mutation UpdateAssetAlert(
    $alertId: ID!
    $expireAt: AWSDateTime!
    $value: Float!
    $name: String!
  ) {
    updateAssetAlert(
      alertId: $alertId
      input: { expireAt: $expireAt, name: $name, value: $value }
    ) {
      ...AssetAlert
    }
  }
  ${FRAGMENT_ASSET_ALERT}
`;

export const UPDATE_ALERT_NOTIFICATION_SETTINGS = gql`
  mutation UpdateAlertNotificationSettings(
    $newFeature: Boolean!
    $priceAlert: Boolean!
    $stopLossAlert: Boolean!
    $stopProfitAlert: Boolean!
    $strategyEnd: Boolean!
  ) {
    updateAlertNotificationSettings(
      input: {
        newFeature: $newFeature
        priceAlert: $priceAlert
        stopLossAlert: $stopLossAlert
        stopProfitAlert: $stopProfitAlert
        strategyEnd: $strategyEnd
      }
    ) {
      newFeature
      priceAlert
      stopLossAlert
      stopProfitAlert
      strategyEnd
    }
  }
`;

/* REFERRAL */

export const CHECK_REFERRAL_CODE = gql`
  query checkReferralCode($referralCode: String!) {
    checkReferralCode(referralCode: $referralCode) {
      status
      discountPercentage
    }
  }
`;

export const CREATE_REFERRAL_CODE = gql`
  mutation createReferralCode(
    $ownerCreditPercentage: Int!
    $redeemerCreditPercentage: Int!
    $ownerProfileId: ID!
  ) {
    createReferralCode(
      ownerCreditPercentage: $ownerCreditPercentage
      redeemerCreditPercentage: $redeemerCreditPercentage
      ownerProfileId: $ownerProfileId
    ) {
      id
      code
      ownerCreditPercentage
      redeemerCreditPercentage
    }
  }
`;

export const GET_REFERRAL_BALANCE = gql`
  query getReferralBalance {
    getReferralBalance {
      total
      redeemed
      available
    }
  }
`;

export const GET_SIGNUP_REFERRAL_CODE = gql`
  query getSignupReferralCode {
    getSignupReferralCode {
      id
      owner
      ownerUser {
        id
        name
      }
      redeemed
      redeemerId
      ownerCredit
      ownerCreditPercentage
      redeemerCredit
      redeemerCreditPercentage
      referralCodeId
      subscribedAt
    }
  }
`;

export const LIST_REDEEMED_REFERRAL_CODES = gql`
  query listRedeemedReferralCodes {
    listRedeemedReferralCodes(limit: 10) {
      items {
        id
        membershipPlan {
          title
        }
        ownerCredit
        ownerCreditPercentage
        redeemed
        redeemedAt
        redeemerCredit
        redeemerCreditPercentage
        referralCodeId
        subscribedAt
        updatedAt
        ownerUser {
          name
        }
        redeemerUser {
          name
        }
      }
      nextToken
    }
  }
`;

export const LIST_REFERRAL_CREDITS = gql`
  query listReferralCredits {
    listReferralCredits {
      items {
        id
        membershipName
        owner
        usedAt
        amount
        discount
      }
    }
  }
`;

/* ASSETS */

export const GET_ASSET_VALUE = gql`
  query getAssetValue($assetId: ID!, $date: AWSDate!) {
    getAssetValue(assetId: $assetId, date: $date) {
      date
      eur
      usd
      cur
    }
  }
`;

export const GET_ASSET_HISTORY = gql`
  query getAssetHistory(
    $assetId: ID!
    $startDate: AWSDate!
    $endDate: AWSDate!
    $interval: DateInterval
    $last: Boolean
  ) {
    getAssetHistory(
      assetId: $assetId
      startDate: $startDate
      endDate: $endDate
      interval: $interval
      last: $last
    ) {
      date
      eur
      usd
      cur
    }
  }
`;

export const LIST_DEFAULT_AVATAR = gql`
  query listDefaultAvatar {
    listDefaultAvatar {
      items {
        id
        filename
        type
        size {
          avatarSmall
          avatarLarge
          preview
          original
        }
      }
    }
  }
`;

export const GET_TOTP = gql`
  query getTOTP {
    getTOTP {
      secret
      qr
    }
  }
`;

export const SEARCH = gql`
  query search($term: String!, $limit: Int) {
    search(term: $term, limit: $limit) {
      ... on Strategy {
        __typename
        id
        name
        color
      }
      ... on Asset {
        __typename
        ...AssetEssentialFields
      }
    }
  }
  ${FRAGMENT_ASSET_ESSENTIAL_FIELDS}
`;

/* BANK */

export const GET_PLAID_LINK_TOKEN = gql`
  query getLinkToken {
    getLinkToken
  }
`;

export const GET_INSTITUTIONS_LIST = gql`
  query getInstitutionsList($profileId: ID!) {
    getInstitutionsList(profileId: $profileId) {
      id
      bankName
    }
  }
`;

export const GET_INSTITUTIONS_ASSETS = gql`
  query getInstitutionAssets($profileId: ID!, $itemId: ID!) {
    getInstitutionAssets(profileId: $profileId, itemId: $itemId) {
      id
      bankName
      currency
      mask
      name
      price
      strategyAssetCategoryId
      quantity
    }
  }
`;

export const SET_PLAID_ACCESS_TOKEN = gql`
  mutation setAccessToken($profileId: ID!, $publicToken: String!) {
    setAccessToken(profileId: $profileId, publicToken: $publicToken)
  }
`;

export const ADD_PROPERTY_ESTIMATION = gql`
  mutation addPropertyEstimation(
    $id: ID!
    $profileId: ID!
    $strategyId: ID!
    $strategyAssetCategoryId: ID!
    $entryPrice: Float!
    $purchasedAt: AWSDate!
  ) {
    addPropertyEstimation(
      input: {
        id: $id
        profileId: $profileId
        strategyId: $strategyId
        strategyAssetCategoryId: $strategyAssetCategoryId
        entryPrice: $entryPrice
        purchasedAt: $purchasedAt
      }
    ) {
      id
    }
  }
`;

export const ENABLE_TOTP = gql`
  mutation enableTOTP($code: String!) {
    enableTOTP(code: $code) {
      status
    }
  }
`;

export const DISABLE_TOTP = gql`
  mutation disableTOTP {
    disableTOTP {
      status
    }
  }
`;

/* MEDIA */

export const CREATE_MEDIA_UPLOAD = gql`
  mutation createMediaUpload(
    $filename: String!
    $mediaType: MediaType!
    $refId: ID!
  ) {
    createMediaUpload(
      filename: $filename
      mediaType: $mediaType
      refId: $refId
    ) {
      id
      key
      filename
      uploadUrl
    }
  }
`;

export const COMPLETE_MEDIA_UPLOAD = gql`
  mutation completeMediaUpload($mediaId: ID!) {
    completeMediaUpload(mediaId: $mediaId) {
      id
    }
  }
`;

/* CONTACTS */

export const CONTACT_BUG_POSTS = gql`
  mutation bugContactRequest($text: String!, $attachments: [ID]) {
    bugContactRequest(text: $text, attachments: $attachments) {
      id
      type
      text
      attachments {
        items {
          id
        }
      }
      createdAt
    }
  }
`;

export const CONTACT_FEATURE_POSTS = gql`
  mutation featureContactRequest($text: String!) {
    featureContactRequest(text: $text) {
      id
      type
      text
    }
  }
`;

export const CONTACT_WIDGETREQUEST_POSTS = gql`
  mutation widgetContactRequest($text: String!) {
    widgetContactRequest(text: $text) {
      id
      type
      text
    }
  }
`;

/* PROPERTY */

export const GET_PROPERTY_ESTIMATION = gql`
  query getPropertyEstimation($address: String!) {
    getPropertyEstimation(address: $address) {
      id
      address
      streetName
      houseNumber
      city
      state
      zestimate
      zillowUrl
    }
  }
`;

/* PAGE */

export const GET_PAGE_BY_SLUG = gql`
  query getPageBySlug($slug: String!) {
    getPageBySlug(slug: $slug) {
      id
      title
      slug
      content
      active
      translations {
        content
        language
        title
      }
      createdAt
      updatedAt
      version
    }
  }
`;

/* FAVORITES */

export const LIST_ASSET_FAVORITES = gql`
  query listAssetFavorites {
    listAssetFavorites {
      items {
        asset {
          ...AssetEssentialFields
          country
          history {
            date
            eur
            usd
            cur
          }
          info {
            id
            data
          }
          marketCap
          marketPos
          providerId
        }
      }
      nextToken
    }
  }
  ${FRAGMENT_ASSET_ESSENTIAL_FIELDS}
`;

export const TOGGLE_LIST_FAVORITES = gql`
  mutation toggleFavoriteAsset($assetId: ID!) {
    toggleFavoriteAsset(assetId: $assetId) {
      status
    }
  }
`;

/* EVENTS */

export const LIST_EVENTS = gql`
  query listEvents {
    listEvents {
      items {
        active
        endDate
        id
        startDate
        title
        type
      }
    }
  }
`;

/* NFT */

export const ADD_NFT_TOKEN = gql`
  mutation addNftToken(
    $profileId: ID!
    $strategyId: ID!
    $strategyAssetCategoryId: ID!
    $collectionId: ID!
    $tokenId: ID!
    $entryPrice: Float!
    $floorPrice: Float!
    $purchasedAt: AWSDate!
    $marketplace: String
  ) {
    addNftToken(
      input: {
        profileId: $profileId
        strategyId: $strategyId
        strategyAssetCategoryId: $strategyAssetCategoryId
        collectionId: $collectionId
        tokenId: $tokenId
        entryPrice: $entryPrice
        floorPrice: $floorPrice
        purchasedAt: $purchasedAt
        marketplace: $marketplace
      }
    ) {
      id
    }
  }
`;

export const GET_NFT_COLLECTIONS_BY_RANKS = gql`
  query getNftCollectionsByRanks($limit: Int, $nextToken: String = null) {
    getNftCollectionsByRanks(limit: $limit, nextToken: $nextToken) {
      nextToken
      items {
        name
        floorSale {
          oneDay
          sevenDay
        }
        imageUrl
        address
        volume {
          oneDay
          sevenDay
        }
        floorAskPrice
      }
    }
  }
`;

export const LIST_STRATEGY_NFT_COLLECTIONS = gql`
  query listStrategyNftCollections(
    $profileId: ID!
    $strategyId: ID!
    $nextToken: String = null
  ) {
    listStrategyNftCollections(
      profileId: $profileId
      strategyId: $strategyId
      nextToken: $nextToken
    ) {
      items {
        id
        strategyId
        tokenCount
        collection {
          name
          address
          imageUrl
          bannerImageUrl
        }
        tokens {
          items {
            collectionAddress
            id
            token {
              name
              tokenId
              floorAskPrice
              floorAskPriceUsd
              imageUrl
            }
            strategyAsset {
              id
              entryPrice
            }
          }
        }
      }
    }
  }
`;

export const SEARCH_COLLECTIONS = gql`
  query searchCollections($name: String!, $limit: Int = 10) {
    searchCollections(name: $name, limit: $limit) {
      collectionId
      name
      contract
      image
    }
  }
`;

export const GET_NFT_TOKEN_PRICE = gql`
  query getNftTokenPrice($address: String!, $tokenId: Int!) {
    getNftTokenPrice(address: $address, tokenId: $tokenId)
  }
`;

/* WALLETS */

export const LIST_WALLETS = gql`
  query listWallets($profileId: ID!) {
    listWallets(profileId: $profileId) {
      address
      id
      logo
      name
    }
  }
`;

export const LIST_WALLET_ASSETS = gql`
  query listWalletAssets($accountId: ID!, $profileId: ID!) {
    listWalletAssets(accountId: $accountId, profileId: $profileId) {
      symbol
      name
      unit
      value
      categoryId
      updatedAt
    }
  }
`;

export const WALLET_CONNECT = gql`
  mutation walletConnect(
    $provider: String!
    $language: Language
    $profileId: ID!
  ) {
    walletConnect(
      provider: $provider
      language: $language
      profileId: $profileId
    ) {
      provider
      token
      connectUrl
    }
  }
`;

/* WIDGETS */

export const WIDGET_YOUTUBE_SEARCH = gql`
  query widgetYoutube($search: [String]!, $nextToken: String) {
    widgetYoutube(search: $search, nextToken: $nextToken) {
      items {
        id
        channelId
        permalink
        channelTitle
        description
        publishTime
        publishedAt
        thumbnails {
          high {
            url
            width
            height
          }
        }
        title
      }
      nextToken
      count
    }
  }
`;

export const GET_FILTERED_ASSETS = gql`
  query getFilteredAssets($filter: SearchType!, $nextToken: String) {
    getFilteredAssets(filter: $filter, nextToken: $nextToken) {
      id
      items {
        ...AssetMainFields
      }
    }
  }
  ${FRAGMENT_ASSET_MAIN_FIELDS}
`;

export const GET_TRENDING_ASSETS = gql`
  query getTrendingAssets {
    getTrendingAssets {
      id
      asset {
        ...AssetMainFields
      }
    }
  }
  ${FRAGMENT_ASSET_MAIN_FIELDS}
`;

export const GET_INSPIRED_ASSETS = gql`
  query getInspiredAssets {
    getInspiredAssets {
      id
      asset {
        currency
        history {
          date
          eur
          usd
          cur
        }
        id
        info {
          id
          data
        }
        logo
        marketPos
        name
        providerId
        symbol
        type
      }
    }
  }
`;

export const GET_NEWS_CALENDAR = gql`
  query getNewsCalendar(
    $startDate: AWSDateTime!
    $endDate: AWSDateTime!
    $eventClass: [String]
    $tickers: [String]
    $limit: Int
    $onlyKeyEvents: Boolean
  ) {
    getNewsCalendar(
      eventType: "IEP"
      startDate: $startDate
      endDate: $endDate
      eventClass: $eventClass
      tickers: $tickers
      limit: $limit
      onlyKeyEvents: $onlyKeyEvents
    ) {
      items {
        id
        dateGmt
        eventUrl
        description
        organizationName
        longDescription
      }
    }
  }
`;

export const GET_NEWS_CALENDAR_FILTERS = gql`
  query getNewsCalendarFilters {
    getNewsCalendarFilters {
      description
      code
      type
    }
  }
`;

export const WIDGET_TWITTER = gql`
  query widgetTwitter($search: [String]!) {
    widgetTwitter(search: $search) {
      count
      nextToken
      tweets {
        createdAt
        favoriteCount
        id
        permalink
        retweet
        screenName
        text
        userImage
        userName
        verified
      }
    }
  }
`;

export const WIDGET_REDDIT = gql`
  query widgetReddit($search: [String]!) {
    widgetReddit(search: $search) {
      count
      nextToken
      posts {
        subreddit
        title
        name
        upvoteRatio
        ups
        score
        createdAt
        thumbnail
        id
        numComments
        author
        permalink
      }
    }
  }
`;

export const GET_NEWS = gql`
  query getNews($tickers: [String]!) {
    getNews(tickers: $tickers) {
      items {
        id
        title
        excerpt
        source
        permalink
      }
    }
  }
`;

export const WIDGET_WIKIPEDIA = gql`
  query widgetWikipedia($search: [String]!) {
    widgetWikipedia(search: $search) {
      items {
        id
        title
        extract
        permalink
      }
      tags
    }
  }
`;

export const WIDGET_WIKIPEDIA_INSPIRED_TAGS = gql`
  query widgetWikipediaInspiredTags {
    widgetWikipediaInspiredTags {
      tags
    }
  }
`;

export const WIDGET_CRYPTO_NEWS = gql`
  query widgetCryptoNews {
    widgetCryptoNews {
      count
      lastUpdateAt
      posts {
        id
        category
        createdAt
        excerpt
        permalink
        thumbnail
        title
      }
    }
  }
`;

export const LIST_ASSETS_BY_TYPE = gql`
  query listAssetsByType($type: AssetType!, $limit: Int, $nextToken: String) {
    listAssetsByType(type: $type, limit: $limit, nextToken: $nextToken) {
      items {
        ...AssetEssentialFields
        country
        history {
          date
          eur
          usd
          cur
        }
        info {
          id
          data
        }
        marketCap
        marketPos
        providerId
      }
      nextToken
    }
  }
  ${FRAGMENT_ASSET_ESSENTIAL_FIELDS}
`;

export const LIST_EXPLORER_ASSETS = gql`
  query listExplorerAssets($type: AssetType!, $limit: Int) {
    listExplorerAssets(type: $type, limit: $limit) {
      items {
        ...AssetEssentialFields
        recent {
          usd
        }
        marketCap
        volume
        industry
        pricePerformanceDaily
        volumePerformanceDaily
        pricePerformanceWeekly
        volumePerformanceWeekly
        weeklyHighest
        weeklyLowest
      }
    }
  }
  ${FRAGMENT_ASSET_ESSENTIAL_FIELDS}
`;

export const LIST_ASSETS_EXCHANGES = gql`
  query listAssetsExchanges {
    listAssetsExchanges {
      code
      country
      name
    }
  }
`;

export const SEARCH_ASSET_BY_TYPE_AND_EXCHANGE = gql`
  query searchAssetsByTypeAndExchange(
    $type: AssetType!
    $exchange: [String]
    $limit: Int
    $nextToken: String
  ) {
    searchAssetsByTypeAndExchange(
      type: $type
      exchange: $exchange
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        ...AssetEssentialFields
        country
        history {
          date
          eur
          usd
          cur
        }
        info {
          id
          data
        }
        marketCap
        marketPos
        providerId
      }
      nextToken
    }
  }
  ${FRAGMENT_ASSET_ESSENTIAL_FIELDS}
`;

export const GET_ASSET = gql`
  query getAsset($id: ID!) {
    getAsset(id: $id) {
      id
      logo
      currency
      country
      region
      industry
      info {
        id
        data
      }
      name
      providerId
      symbol
      type
      historicalIndex
      exchange
      history {
        date
        eur
        usd
        cur
      }
      marketCap
      volume
    }
  }
`;

export const VOLUMES_VARIATIONS_BY_INTERVAL = gql`
  query getVolumeVariations($strategyId: ID!) {
    getVolumeVariations(strategyId: $strategyId) {
      ...Variations
    }
  }
  ${FRAGMENT_VARIATIONS}
`;

export const PRICES_VARIATIONS_BY_INTERVAL = gql`
  query getPriceVariations($strategyId: ID!) {
    getPriceVariations(strategyId: $strategyId) {
      ...Variations
    }
  }
  ${FRAGMENT_VARIATIONS}
`;

export const SEARCH_ASSETS = gql`
  query searchAssets($term: String!, $type: AssetType) {
    searchAssets(term: $term, type: $type) {
      items {
        ...AssetEssentialFields
      }
    }
  }
  ${FRAGMENT_ASSET_ESSENTIAL_FIELDS}
`;

export const REQUEST_USER_DATA = gql`
  query requestUserData {
    requestUserData {
      status
    }
  }
`;

export const GET_STRATEGY_DATA = gql`
  query getStrategyData($strategyId: ID!) {
    getStrategyData(strategyId: $strategyId) {
      status
      url
    }
  }
`;

export const LIST_QR_AUTHS = gql`
  query listQRAuths {
    listQRAuths {
      items {
        ...QRAuthFields
      }
      nextToken
    }
  }
  ${FRAGMENT_QR_AUTH_FIELDS}
`;

export const REQUEST_QR_AUTH = gql`
  mutation RequestQRAuth {
    requestQRAuth {
      createdAt
      device
      id
      qr
      status
    }
  }
`;

export const DELETE_QR_AUTH = gql`
  mutation DeleteQRAuth($id: ID!) {
    deleteQRAuth(id: $id) {
      status
    }
  }
`;

export const CANCEL_MEMBERSHIP = gql`
  mutation cancelMembership {
    cancelMembership {
      id
      paid
      active
    }
  }
`;

export const REQUEST_USER_REMOVAL = gql`
  mutation requestRemoval {
    requestRemoval {
      bypassToken
      expireAt
      membershipId
      reason
      removalStatus
    }
  }
`;

export const COMPLETE_USER_REMOVAL_VERIFICATION = gql`
  mutation completeUserRemovalVerification(
    $bypassToken: String!
    $code: String!
  ) {
    completeUserRemovalVerification(bypassToken: $bypassToken, code: $code) {
      status
    }
  }
`;

export const CANCEL_USER_REMOVAL_REQUEST = gql`
  mutation cancelUserRemovalRequest {
    cancelUserRemovalRequest {
      status
    }
  }
`;

export const TAKE_SNAPSHOT = gql`
  mutation takeSnapshot($data: String!, $width: Int!, $height: Int!) {
    takeSnapshot(data: $data, width: $width, height: $height) {
      id
      img
      filename
      createdAt
    }
  }
`;
